import { useCallback, useEffect, useRef, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { Plus, UploadSimple, ShieldCheck, FileZip, CheckCircle, Circle } from "@phosphor-icons/react";
import { downloadAuthFile } from "../../lib/fileOpen";
import HsPackList from "./HsPackList";
import HsPackPickerDialog from "./HsPackPickerDialog";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export default function ContractHsPackTab({ contract }) {
  const [items, setItems] = useState(null);
  const [receipts, setReceipts] = useState(null);
  const [pick, setPick] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showReceipts, setShowReceipts] = useState(null); // item or "all"
  const fileRef = useRef(null);

  const load = useCallback(() => {
    API.get(`/contracts/${contract.id}/hs-pack`).then(({ data }) => setItems(data.items)).catch(() => toast.error("Could not load the H&S pack"));
    API.get(`/contracts/${contract.id}/hs-pack/receipts`).then(({ data }) => setReceipts(data)).catch(() => setReceipts(null));
  }, [contract.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      await API.post(`/contracts/${contract.id}/hs-pack/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${f.name} added to the H&S pack`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.title || it.kind_label}" from this contract's H&S pack?`)) return;
    try { await API.delete(`/contracts/${contract.id}/hs-pack/${it.id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not remove"); }
  };

  const toggleVisible = async (it) => {
    const next = it.client_visible === false;
    try {
      await API.put(`/contracts/${contract.id}/hs-pack/${it.id}`, { client_visible: next });
      setItems(list => list.map(x => (x.id === it.id ? { ...x, client_visible: next } : x)));
      toast.success(next ? "Now visible to the client" : "Hidden from the client");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not update"); }
  };

  const visible = (items || []).filter(i => i.client_visible !== false).length;
  const receiptMap = receipts ? Object.fromEntries(receipts.items.map(i => [i.id, i])) : null;
  const ops = receipts?.operatives || [];
  const outstanding = receipts ? receipts.items.reduce((n, i) => n + i.unread.length, 0) : 0;

  return (
    <div className="space-y-4" data-testid="contract-hs-pack">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold font-[Chivo] flex items-center gap-1.5"><ShieldCheck size={16} className="text-emerald-700" /> Health &amp; safety pack</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Everything the team needs on site for this contract in one place. {items ? <>{items.length} document{items.length === 1 ? "" : "s"} · {visible} visible to the client · operatives must confirm reading each one before their first visit.</> : "Loading…"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {items?.length > 0 && <Button variant="outline" size="sm" onClick={() => downloadAuthFile(`/contracts/${contract.id}/hs-pack.zip`, `${contract.reference}_HS_pack.zip`)} data-testid="hs-pack-zip-btn"><FileZip size={14} className="mr-1" /> Download pack</Button>}
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={upload} data-testid="hs-pack-upload-input" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="hs-pack-upload-btn"><UploadSimple size={14} className="mr-1" /> {uploading ? "Uploading…" : "Upload PDF"}</Button>
          <Button size="sm" onClick={() => setPick(true)} data-testid="hs-pack-add-btn"><Plus size={14} className="mr-1" /> Add documents</Button>
        </div>
      </div>

      {receipts && items?.length > 0 && ops.length > 0 && (
        <button type="button" onClick={() => setShowReceipts("all")} data-testid="hs-pack-receipts-summary"
          className={`w-full text-left text-xs rounded-md border px-3 py-2 flex items-center gap-2 ${outstanding ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
          {outstanding ? <Circle size={14} /> : <CheckCircle size={14} weight="fill" />}
          <span className="flex-1">{outstanding ? <><strong>{outstanding} read confirmation{outstanding === 1 ? "" : "s"} outstanding</strong> across {ops.length} operative{ops.length === 1 ? "" : "s"} — tap to see who still needs to read what.</> : <>Every operative on this contract ({ops.length}) has confirmed reading every document.</>}</span>
          <span className="underline underline-offset-2">Read receipts</span>
        </button>
      )}

      {items && (
        <HsPackList items={items} pdfUrl={(it) => `/contracts/${contract.id}/hs-pack/${it.id}/pdf`} onRemove={remove} onToggleVisible={toggleVisible} receipts={receiptMap} onShowReceipts={(it) => setShowReceipts(it)}
          emptyText="No documents yet — add the RAMS, COSHH assessments, data sheets and policies that apply to this contract so the team and the client can open them any time." testid="contract-hs-pack-list" />
      )}
      <HsPackPickerDialog open={pick} onOpenChange={setPick} contractId={contract.id} existing={items || []} onAdded={() => load()} />

      <Dialog open={!!showReceipts} onOpenChange={(o) => !o && setShowReceipts(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="hs-pack-receipts-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Read receipts</DialogTitle>
            <DialogDescription>{ops.length ? `Operatives on this contract: ${ops.map(o => o.name).join(", ")}.` : "No operatives are assigned to this contract yet — receipts appear once a team is set or a visit is crewed."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(receipts?.items || []).filter(i => showReceipts === "all" || i.id === showReceipts?.id).map(i => (
              <div key={i.id} className="border border-border rounded-lg p-3" data-testid={`hs-pack-receipt-row-${i.id}`}>
                <p className="text-sm font-medium">{i.title}</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {i.read.map(o => <span key={o.user_id} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-0.5 text-[11px]" title={fmtWhen(o.read_at)}><CheckCircle size={12} weight="fill" /> {o.name} · {fmtWhen(o.read_at)}</span>)}
                  {i.unread.map(o => <span key={o.user_id} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${o.reissued ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid={`hs-pack-receipt-unread-${i.id}-${o.user_id}`}><Circle size={12} /> {o.name} · {o.reissued ? "read an older version — re-read needed" : "not yet"}</span>)}
                  {!i.read.length && !i.unread.length && <span className="text-[11px] text-muted-foreground">No operatives to read this yet.</span>}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
